import { appsignal } from './appsignal';

class FetchUtils {
  static get GENERAL_ERROR_MESSAGE() {
    return 'Something went wrong. Please try again later.';
  }
  static async checkResponseStatus(response) {
    if (response.status === 401) {
      // Session expired, so reload will save the last page address
      // and move to login page.
      location.reload();
      return response;
    }
    if (response.status === 404) {
      return Promise.reject({ smErrText: 'Page not found.', status: 404 });
    }
    if (response.status === 422) {
      let payload = null;
      try {
        payload = await response.json();
      } catch {
        // Fall back to the generic validation message when the response body is not JSON.
      }
      return Promise.reject({
        status: 422,
        payload,
        smErrText: payload?.errors?.[0] || FetchUtils.GENERAL_ERROR_MESSAGE
      });
    }
    if (!response.ok) {
      return Promise.reject({ smErrText: FetchUtils.GENERAL_ERROR_MESSAGE, status: response.status });
    }
    return response;
  }

  /** True when the error was produced by checkResponseStatus for a 422 response. */
  static isValidationError(err) {
    return err?.status === 422;
  }

  static handleResponseError(err) {
    console.log(err);
    appsignal.sendError(new Error(err.smErrText ? err.smErrText : err.message));
  }

  static CSRFToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  }

  static async fetchJSON(url) {
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': FetchUtils.CSRFToken()
      }
    });
    await FetchUtils.checkResponseStatus(response);
    return response.json();
  }

  /**
   * Generic form submit handler using fetch
   * @param {HTMLFormElement} form - The form element to submit
   * @param {Object} options - Configuration options
   * @param {Function} options.onSuccess - Callback for successful save (no validation errors)
   * @param {Function} options.onFailure - Callback for validation errors (receives response with has_errors: true)
   * @param {Function} options.onError - Callback for HTTP errors (receives error)
   * @param {Function} options.beforeSubmit - Optional callback before submit (can return false to cancel)
   * @param {string} options.errorMessage - Custom error message to display on HTTP error
   * @returns {Function} Event handler function
   */
  static handleFormSubmit(form, options = {}) {
    return async (e) => {
      e.preventDefault();
      // Prevent Rails UJS from also handling this submit
      e.stopImmediatePropagation();

      // Call beforeSubmit hook if provided
      if (options.beforeSubmit && options.beforeSubmit() === false) {
        return;
      }

      const formData = new FormData(form);
      const url = form.action;
      const method = form.method.toUpperCase();

      try {
        const response = await fetch(url, {
          method: method,
          body: formData,
          headers: {
            'X-CSRF-Token': FetchUtils.CSRFToken(),
            'Accept': 'application/json'
          }
        });

        await FetchUtils.checkResponseStatus(response);
        const result = await response.json();

        // Handle validation errors vs success
        if (result.has_errors) {
          if (options.onFailure) options.onFailure(result);
        } else {
          if (options.onSuccess) options.onSuccess(result);
        }
      } catch (err) {
        const errorMessage = options.errorMessage || 'There was an error while saving the form!';
        alert(errorMessage);

        if (options.onError) options.onError(err);

        FetchUtils.handleResponseError(err);
      }
    };
  }
}

export default FetchUtils;
